import apiClient from './client';

export interface DocumentPermission {
  id: string;
  documentId: string;
  tenantId: string;
  permissionType: 'public' | 'department' | 'user';
  departmentId?: string;
  departmentName?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  canView: boolean;
  canSearch: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canDownload: boolean;
  createdAt: string;
  updatedAt: string;
  grantedBy?: string;
}

export interface DocumentAccessSummary {
  documentId: string;
  isPublic: boolean;
  publicPermissions?: {
    canView: boolean;
    canSearch: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canDownload: boolean;
  };
  departmentCount: number;
  userCount: number;
  departments: Array<{
    id: string;
    name: string;
    canView: boolean;
    canSearch: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canDownload: boolean;
  }>;
  users: Array<{
    id: string;
    email: string;
    name: string;
    canView: boolean;
    canSearch: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canDownload: boolean;
  }>;
}

export interface SetPermissionInput {
  permissionType: 'public' | 'department' | 'user';
  departmentId?: string;
  userId?: string;
  canView?: boolean;
  canSearch?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canDownload?: boolean;
}

export interface PublicAccessInput {
  canView?: boolean;
  canSearch?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canDownload?: boolean;
}

class PermissionsService {
  async getDocumentPermissions(documentId: string): Promise<{ permissions: DocumentPermission[] }> {
    const response = await apiClient.get(`/documents/${documentId}/permissions`);
    return response.data;
  }

  async getDocumentPermissionSummary(documentId: string): Promise<DocumentAccessSummary> {
    const response = await apiClient.get(`/documents/${documentId}/permissions/summary`);
    return response.data;
  }

  async setPermission(documentId: string, input: SetPermissionInput): Promise<DocumentPermission> {
    const response = await apiClient.post(`/documents/${documentId}/permissions`, input);
    return response.data;
  }

  async bulkSetPermissions(
    documentId: string,
    permissions: SetPermissionInput[],
    replaceAll: boolean = false
  ): Promise<{ permissions: DocumentPermission[] }> {
    const response = await apiClient.put(`/documents/${documentId}/permissions`, {
      permissions,
      replaceAll,
    });
    return response.data;
  }

  async removePermission(documentId: string, permissionId: string): Promise<void> {
    await apiClient.delete(`/documents/${documentId}/permissions/${permissionId}`);
  }

  async checkPermission(
    documentId: string,
    permission: 'view' | 'search' | 'edit' | 'delete' | 'download'
  ): Promise<{ hasPermission: boolean }> {
    const response = await apiClient.get(
      `/documents/${documentId}/permissions/check?permission=${permission}`
    );
    return response.data;
  }

  async setPublicAccess(documentId: string, input: PublicAccessInput): Promise<DocumentPermission> {
    const response = await apiClient.post(`/documents/${documentId}/permissions/public`, input);
    return response.data;
  }

  async removePublicAccess(documentId: string): Promise<void> {
    await apiClient.delete(`/documents/${documentId}/permissions/public`);
  }
}

export const permissionsService = new PermissionsService();
